import type { SavedViewResponse } from '@domain/schemas/saved-view.schema';
import type { QueryFilter } from '@domain/types/filters';
import { getRequestAuthPayload } from '@helpers/getRequestAuth';
import type { QueryClient } from '@tanstack/react-query';
import { queryOptions, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import type { QueryFactory } from './factory.types';
import { fetchSubmissionsCount } from './fetchSubmissions';

export type SavedView = SavedViewResponse;

export type SavedViewCountInput = { id: string; filters: QueryFilter[] };

// Polling is the backstop for contract changes no client-side hook can observe (other
// users, editor saves); refetchInterval pauses in background tabs by default.
const SAVED_VIEW_COUNT_STALE_TIME_MS = 15 * 1000;
const SAVED_VIEW_COUNT_REFETCH_INTERVAL_MS = 30 * 1000;
export const SAVED_VIEW_COUNTS_QUERY_KEY = ['saved-view-counts'] as const;

export const fetchSavedViews = async (): Promise<SavedView[]> => {
  const networkPayload = await getRequestAuthPayload({ method: 'get', url: '/saved-views' });
  const response = await axios(networkPayload);
  return response.data;
};

export const savedViewQueries = {
  list: () => queryOptions({ queryKey: ['saved-views'] as const, queryFn: fetchSavedViews, staleTime: 60_000 }),
  count: (view: SavedViewCountInput) =>
    queryOptions({
      queryKey: [...SAVED_VIEW_COUNTS_QUERY_KEY, view.id] as const,
      queryFn: () => fetchSubmissionsCount(view.filters),
      staleTime: SAVED_VIEW_COUNT_STALE_TIME_MS,
      refetchInterval: SAVED_VIEW_COUNT_REFETCH_INTERVAL_MS,
    }),
} satisfies QueryFactory;

export const invalidateSavedViewCounts = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: SAVED_VIEW_COUNTS_QUERY_KEY });

export function useSavedViews(enabled = true) {
  return useQuery({ ...savedViewQueries.list(), enabled });
}

export function useSavedViewCounts(views: SavedViewCountInput[], enabled = true) {
  const queryClient = useQueryClient();
  return useQueries({
    queries: views.map((view) => ({ ...savedViewQueries.count(view), enabled })),
    combine: (results) => ({
      data: Object.fromEntries(
        results.flatMap((result, i) => (result.data === undefined ? [] : [[views[i].id, result.data]])),
      ) as Record<string, number>,
      fetching: Object.fromEntries(
        views.map((view, i) => [
          view.id,
          results[i].isFetching &&
            (queryClient.getQueryState(savedViewQueries.count(view).queryKey)?.isInvalidated ?? false),
        ]),
      ) as Record<string, boolean>,
    }),
  });
}
